/* eslint-disable no-async-promise-executor */
/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import _ from 'lodash';

import { CAPIWS } from '@/app/vendors/e-imzo';
import { getTimestamp, joinSignatures } from '@/entities/dms';
import { Cert } from '../model/types';
import parseEimzoKey from '@/app/helpers/parseEimzoKey';

dayjs.extend(customParseFormat);

/** Формат срока действия сертификата в parsedAlias: 2026.01.01 23:59:59 */
const CERT_VALIDTO_FORMAT = 'YYYY.MM.DD';

interface IGetCertsRes {
  certificates: Cert[];
}

interface TimestampResponse {
  isAttachedPkcs7: boolean;
  success: boolean;
  timeStampTokenB64: string;
  reason?: string;
}

const timestamper = (
  signatureHex: string,
  pkcs7: string,
  callback: (args: TimestampResponse) => void,
  onReject: (args: any) => void
) => {
  getTimestamp(signatureHex, pkcs7)
    .then(({ data }) => {
      if (data.success) {
        callback(data);
      } else {
        return Promise.reject(data?.reason);
      }
    })
    .catch((err) => {
      // showAlert('error', err.response && err.response.statusText);
      return onReject(err?.response?.statusText);
    });
};

interface EimzoVersion {
  success: boolean;
  major: string;
  minor: string;
  patch: string;
}

export const getVersion = (): Promise<EimzoVersion> =>
  new Promise((resolve, reject) => {
    CAPIWS.version(
      (_event: any, data: EimzoVersion) => {
        resolve(data);
      },
      (error: any) => {
        reject(error);
      }
    );
  });

let cachedEimzoMajor: number | undefined;
const getEimzoMajor = async (): Promise<number> => {
  if (cachedEimzoMajor !== undefined) return cachedEimzoMajor;
  try {
    const v = await getVersion();
    cachedEimzoMajor = Number(v.major) || 0;
  } catch {
    cachedEimzoMajor = 0;
  }
  return cachedEimzoMajor;
};

export const isModernEimzoFlow = async (isUsbLog?: boolean): Promise<boolean> =>
  Boolean(isUsbLog) || (await getEimzoMajor()) > 5;

export const startApi = () => {
  CAPIWS.apikey([
    'localhost',
    '96D0C1491615C82B9A54D9989779DF825B690748224C2B04F500F370D51827CE2644D8D4A82C18184D73AB8530BB8ED537269603F61DB0D03D2104ABF789970B',
    '127.0.0.1',
    'A7BCFA5D490B351BE0754130DF03A068F855DB4333D43921125B9CF2670EF6A40370C646B90401955E1F7BC9CDBF59CE0B2C5467D820BE189C845D0B79CFC96F',
    'finmakon.uz',
    '01E74D25FE4033DDAFF2F6464AB3BC53E872DF8893ADDAA73ACF8EEA8BBF4CE599FF5CCEE7BCD61EBC680888911B1E9E0BA8820833FF56F43E4390D85635AB47',
  ]);
};

export const getAllCertificatesCertkey = () =>
  new Promise<IGetCertsRes>((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'certkey',
        name: 'list_all_certificates',
      },
      (_event: any, data: any) => {
        resolve(data);
      },
      (error: any) => {
        reject(error);
      }
    );
  });

export const getAllCertificatesPfx = () =>
  new Promise<IGetCertsRes>((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'pfx',
        name: 'list_all_certificates',
      },
      (_event: any, data: any) => {
        resolve(data);
      },
      (error: any) => {
        reject(error);
      }
    );
  });

const preLoadKey = (item: any) =>
  new Promise((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'certkey',
        name: 'load_key',
        arguments: [item.disk, item.path, item.name, item.serialNumber],
      },
      (_event: any, data: any) => {
        if (data.success) {
          resolve(data.keyId);
        } else {
          reject(data.reason);
        }
      },
      (e: any) => {
        reject(e);
      }
    );
  });

const postLoadKey = (
  id: any,
  string: string,
  isBase64?: boolean
): Promise<string> => {
  return new Promise((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'pkcs7',
        name: 'create_pkcs7',
        arguments: [
          !isBase64 ? btoa(unescape(encodeURIComponent(string))) : string,
          id,
          'no',
        ],
      },
      (_event: any, data: any) => {
        if (data.success) {
          const pkcs7 = data.pkcs7_64;
          if (timestamper) {
            const sn = data.signer_serial_number;
            timestamper(
              data.signature_hex,
              data.pkcs7_64,
              (tst) => {
                if (tst.isAttachedPkcs7) {
                  return resolve(tst.timeStampTokenB64);
                }
                CAPIWS.callFunction(
                  {
                    plugin: 'pkcs7',
                    name: 'attach_timestamp_token_pkcs7',
                    arguments: [pkcs7, sn, tst.timeStampTokenB64],
                  },
                  (_event2: any, data2: any) => {
                    if (data2.success) {
                      const pkcs7tst = data2.pkcs7_64;
                      resolve(pkcs7tst);
                    } else {
                      reject(data2.reason);
                    }
                  },
                  (e: any) => {
                    reject(e);
                  }
                );
              },
              (err) => {
                reject(err);
              }
            );
          } else {
            resolve(pkcs7);
          }
        } else {
          reject(data.reason);
        }
      },
      (e: any) => {
        reject(e);
      }
    );
  });
};

const postLoadKeyAttach = (id: string, string: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'pkcs7',
        name: 'append_pkcs7_attached',
        arguments: [string, id],
      },
      (_event: any, data: any) => {
        if (data.success) {
          const pkcs7 = data.pkcs7_64;
          if (timestamper) {
            const sn = data.signer_serial_number;
            timestamper(
              data.signature_hex,
              data.pkcs7_64,
              (tst) => {
                if (tst.isAttachedPkcs7) {
                  return resolve(tst.timeStampTokenB64);
                }
                CAPIWS.callFunction(
                  {
                    plugin: 'pkcs7',
                    name: 'attach_timestamp_token_pkcs7',
                    arguments: [pkcs7, sn, tst.timeStampTokenB64],
                  },
                  (_event2: any, data2: any) => {
                    if (data2.success) {
                      const pkcs7tst = data2.pkcs7_64;
                      resolve(pkcs7tst);
                    } else {
                      reject(data2.reason);
                    }
                  },
                  (e: any) => {
                    reject(e);
                  }
                );
              },
              (err) => {
                reject(err);
              }
            );
          } else {
            resolve(pkcs7);
          }
        } else {
          reject(data.reason);
        }
      },
      (e: any) => {
        reject(e);
      }
    );
  });
};

const loadPfxKey = (
  item: any,
  string: string,
  type?: string,
  isBase64?: boolean
): Promise<string> =>
  new Promise((resolve, reject) => {
    CAPIWS.callFunction(
      {
        plugin: 'pfx',
        name: 'load_key',
        arguments: [item.disk, item.path, item.name, item.alias],
      },
      (_event: any, data: any) => {
        if (data.success) {
          const id = data.keyId;
          window.sessionStorage.setItem(item.serialNumber, id);
          const promise =
            type === 'attach'
              ? postLoadKeyAttach(id, string)
              : postLoadKey(id, string, isBase64);
          promise.then(
            (encryptedString) => {
              resolve(encryptedString);
            },
            (e) => {
              reject(e);
            }
          );
        } else {
          reject(data.reason);
        }
      },
      (e: any) => {
        reject(e);
      }
    );
  });

export const getSignature = (
  item: Cert | 'ckc' | 'idcard',
  string: string,
  isBase64?: boolean
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (item === 'ckc' || item === 'idcard') {
      postLoadKey(item, string, isBase64).then(
        (encryptedString) => {
          resolve(encryptedString);
        },
        (error) => {
          reject(error);
        }
      );
    } else if (item.type === 'certkey') {
      preLoadKey(item).then(
        (id) => {
          postLoadKey(id, string, isBase64).then(
            (encryptedString: string) => {
              resolve(encryptedString);
            },
            (error) => {
              reject(error);
            }
          );
        },
        (error) => {
          reject(error);
        }
      );
    } else if (item.type === 'pfx') {
      const id = window.sessionStorage.getItem(item.serialNumber);
      if (id) {
        postLoadKey(id, string, isBase64).then(
          (encryptedString) => {
            resolve(encryptedString);
          },
          () => {
            loadPfxKey(item, string, undefined, isBase64)
              .then((encryptedString) => {
                resolve(encryptedString);
              })
              .catch((e) => {
                reject(e);
              });
          }
        );
      } else {
        loadPfxKey(item, string, undefined, isBase64)
          .then((encryptedString) => {
            resolve(encryptedString);
          })
          .catch((e) => {
            reject(e);
          });
      }
    }
  });

export const getAcceptSignature = (item: any, data: any): Promise<string> =>
  new Promise((resolve, reject) => {
    if (item.type === 'certkey') {
      preLoadKey(item).then(
        (id: any) => {
          postLoadKeyAttach(id, data).then(
            (encryptedString) => {
              resolve(encryptedString);
            },
            (error) => {
              reject(error);
            }
          );
        },
        (error) => {
          reject(error);
        }
      );
    } else if (item.type === 'pfx') {
      const id = window.sessionStorage.getItem(item.serialNumber);
      if (id) {
        postLoadKeyAttach(id, data).then(
          (encryptedString) => {
            resolve(encryptedString);
          },
          () => {
            loadPfxKey(item, data, 'attach').then(resolve, reject);
          }
        );
      } else {
        loadPfxKey(item, data, 'attach').then((encryptedString) => {
          resolve(encryptedString);
        });
      }
    }
  });

const parseValidDate = (date: string) =>
  new Date(date.split(' ')[0].split('.').join(','));

export const sign = async (
  string: string,
  uidOrCert: string | Cert,
  isUsbLog?: boolean
): Promise<string> =>
  new Promise(async (resolve, reject) => {
    if (isUsbLog) {
      try {
        const signature = await getSignature('ckc', string);
        resolve(signature);
      } catch (err) {
        reject(err);
      }
      return;
    }
    const phisPersonPinfl = localStorage.getItem('phisPersonPinfl') || '';
    let cert: Cert;
    if (typeof uidOrCert === 'string') {
      cert = await getCertificate(phisPersonPinfl || uidOrCert).then(
        (res) => res
      );
    } else {
      cert = uidOrCert;
    }
    try {
      const signature = await getSignature(cert, string);
      resolve(signature);
    } catch (err) {
      reject();
    }
  });

export const attach = (
  string: string,
  uid: string,
  customCert?: any,
  isUsbLog?: boolean,
  originalString?: string
): Promise<string> =>
  new Promise(async (resolve, reject) => {
    const phisPersonPinfl = localStorage.getItem('phisPersonPinfl') || '';
    if (originalString) {
      let item: Cert | 'ckc' | 'idcard';
      if (isUsbLog) {
        item = 'ckc';
      } else if (customCert) {
        item = _.cloneDeep(customCert);
      } else {
        try {
          item = await getCertificate(phisPersonPinfl || uid);
        } catch {
          reject();
          return;
        }
      }
      try {
        const newSig = await getSignature(item, originalString, true);
        const { data } = await joinSignatures(string, newSig);
        if (!data.success) {
          reject(new Error(data.message));
          return;
        }
        resolve(data.pkcs7B64);
      } catch (err) {
        reject(err);
      }
      return;
    }
    let cert = _.cloneDeep(customCert);
    if (!cert) {
      try {
        cert = await getCertificate(phisPersonPinfl || uid);
      } catch {
        return reject();
      }
    }
    try {
      const signature = await getAcceptSignature(cert, string);
      resolve(signature);
    } catch {
      // showAlert('error', i18n.PasswordIncorrect);
      reject();
    }
  });

export const getCertificate = (uid: string) =>
  new Promise<Cert>(async (resolve, reject) => {
    // const { uid, serialNumber, accountTin } = store.getState().auth;
    let allCerts: any;
    try {
      allCerts = await getAllCertificates(uid);
      // if (!allCerts || !allCerts?.length) {
      //   allCerts = await getAllCertificates(accountTin);
      // }
    } catch {
      return reject();
    }
    // if (serialNumber) {
    //   resolve(
    //     allCerts.find(
    //       (cert: ICert) =>
    //         (cert.inn === uid || cert.parsedAlias?.['1.2.860.3.16.1.2'] === uid) &&
    //         cert.serialNumber === serialNumber
    //     )
    //   );
    // }
    // else
    const filteredCerts = allCerts.filter(
      (cert: any) =>
        cert.inn === uid || cert.parsedAlias?.['1.2.860.3.16.1.2'] === uid
    );
    switch (filteredCerts.length) {
      case 0:
        // showAlert('error', i18n.CertNotFound);
        reject();
        break;
      case 1:
        resolve(filteredCerts[0]);
        break;
      default:
        resolve(
          filteredCerts.sort((a: any, b: any) =>
            parseValidDate(a.parsedAlias.validto) >
            parseValidDate(b.parsedAlias.validto)
              ? -1
              : 1
          )[0]
        );
    }
  });

export const getAllCertificates = (uid?: string): Promise<Cert[]> =>
  new Promise(async (resolve, reject) => {
    try {
      const { certificates: pfxCerts } = await getAllCertificatesPfx();
      const { certificates: certkeyCerts } = await getAllCertificatesCertkey();
      const certs: Cert[] = [
        ...parseEimzoKey(pfxCerts, 'pfx'),
        ...parseEimzoKey(certkeyCerts, 'certkey'),
      ]
        .map((i) => ({
          ...i,
          overdue:
            new Date() >
            dayjs(i?.parsedAlias?.validto || new Date(), CERT_VALIDTO_FORMAT)
              .add(1, 'day')
              .toDate(),
        }))
        .sort((a, b) => a.overdue - b.overdue);
      // store.dispatch(setEimzoError(false));
      if (uid) {
        const foundCerts = certs.filter(
          (cert) =>
            cert.inn === uid || cert.parsedAlias?.['1.2.860.3.16.1.2'] === uid
        );
        resolve(foundCerts);
      } else {
        resolve(certs);
      }
    } catch (error) {
      // store.dispatch(setEimzoError(true));
      reject(error);
    }
  });
